import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

export type PaginationSize = "sm" | "md" | "lg";

export interface PaginationProps extends Omit<React.HTMLAttributes<HTMLElement>, "onChange"> {
  /** Total number of pages. */
  pageCount: number;
  /** Current page (1-indexed). */
  page: number;
  /** Called when the user picks a new page (1-indexed). */
  onPageChange: (page: number) => void;
  /**
   * Maximum number of page buttons to render before collapsing the middle into
   * an ellipsis. Default 7. Increase for desktop, decrease for narrow contexts.
   */
  siblingCount?: number;
  /** Hide the previous/next buttons. Default false. */
  hideNavigation?: boolean;
  /** Disable the entire control. */
  disabled?: boolean;
  /** Custom labels for the prev/next buttons. */
  previousLabel?: React.ReactNode;
  nextLabel?: React.ReactNode;
  size?: PaginationSize;
  /** Optional totals strip ("Showing 1–10 of 142"). Pass a node to render. */
  totals?: React.ReactNode;
}

/**
 * Build a page list of either page numbers or `'…'` for ellipsis. Always
 * includes page 1 and the last page; surrounds the current page with up to
 * (siblingCount - 4) / 2 neighbors on each side.
 */
function buildPageList(pageCount: number, page: number, siblingCount: number): Array<number | "…"> {
  if (pageCount <= 0) return [];
  if (pageCount <= siblingCount) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const sideCount = Math.max(1, Math.floor((siblingCount - 5) / 2));
  const leftWindowEnd = 2 + sideCount * 2;
  const rightWindowStart = pageCount - 1 - sideCount * 2;

  const list: Array<number | "…"> = [1];

  if (page <= leftWindowEnd) {
    for (let i = 2; i <= leftWindowEnd; i++) list.push(i);
    list.push("…");
  } else if (page >= rightWindowStart) {
    list.push("…");
    for (let i = rightWindowStart; i <= pageCount - 1; i++) list.push(i);
  } else {
    list.push("…");
    for (let i = page - sideCount; i <= page + sideCount; i++) list.push(i);
    list.push("…");
  }
  list.push(pageCount);
  return list;
}

export const Pagination = React.forwardRef<HTMLElement, PaginationProps>(function Pagination(
  {
    className,
    disabled,
    hideNavigation,
    nextLabel,
    onPageChange,
    page,
    pageCount,
    previousLabel,
    siblingCount = 7,
    size = "md",
    totals,
    ...props
  },
  ref,
) {
  const safePage = Math.max(1, Math.min(page, Math.max(1, pageCount)));
  const pageList = React.useMemo(
    () => buildPageList(pageCount, safePage, Math.max(5, siblingCount)),
    [pageCount, safePage, siblingCount],
  );

  const handleSelect = (next: number) => {
    if (disabled) return;
    if (next < 1 || next > pageCount) return;
    if (next === safePage) return;
    onPageChange(next);
  };

  const isFirst = safePage <= 1;
  const isLast = safePage >= pageCount;

  return (
    <nav
      ref={ref}
      aria-label="Pagination"
      className={cx("cw-v2-pagination", `cw-v2-pagination--${size}`, className)}
      {...props}
    >
      {totals ? <div className="cw-v2-pagination__totals">{totals}</div> : null}
      <ul className="cw-v2-pagination__list">
        {!hideNavigation ? (
          <li className="cw-v2-pagination__control-item">
            <button
              type="button"
              className="cw-v2-pagination__control"
              aria-label="Previous page"
              disabled={disabled || isFirst}
              onClick={() => handleSelect(safePage - 1)}
            >
              <ChevronLeft className="cw-v2-pagination__icon" aria-hidden />
              {previousLabel ?? <span className="cw-v2-pagination__sr">Previous</span>}
            </button>
          </li>
        ) : null}

        <li className="cw-v2-pagination__mobile-status" aria-live="polite">
          Page {safePage.toLocaleString()} of {pageCount.toLocaleString()}
        </li>

        {pageList.map((entry, index) =>
          entry === "…" ? (
            // The ellipsis sits between two real page numbers; key by those neighbours so each gap has a stable identity even when the window shifts.
            <li
              key={`ellipsis-${pageList[index - 1] ?? "head"}-${pageList[index + 1] ?? "tail"}`}
              className="cw-v2-pagination__ellipsis-item"
              aria-hidden
            >
              <span className="cw-v2-pagination__ellipsis">
                <MoreHorizontal className="cw-v2-pagination__icon" />
              </span>
            </li>
          ) : (
            <li key={entry} className="cw-v2-pagination__page-item">
              <button
                type="button"
                className="cw-v2-pagination__page"
                aria-current={entry === safePage ? "page" : undefined}
                aria-label={`Page ${entry}`}
                data-active={entry === safePage ? "true" : undefined}
                disabled={disabled}
                onClick={() => handleSelect(entry)}
              >
                {entry}
              </button>
            </li>
          ),
        )}

        {!hideNavigation ? (
          <li className="cw-v2-pagination__control-item">
            <button
              type="button"
              className="cw-v2-pagination__control"
              aria-label="Next page"
              disabled={disabled || isLast}
              onClick={() => handleSelect(safePage + 1)}
            >
              {nextLabel ?? <span className="cw-v2-pagination__sr">Next</span>}
              <ChevronRight className="cw-v2-pagination__icon" aria-hidden />
            </button>
          </li>
        ) : null}
      </ul>
    </nav>
  );
});
Pagination.displayName = "Pagination";

/**
 * Helper that returns the slice of items on the given page.
 * Useful when paging client-side data.
 */
export function paginateClient<T>(items: T[], page: number, pageSize: number): T[] {
  const start = Math.max(0, (page - 1) * pageSize);
  return items.slice(start, start + pageSize);
}

/**
 * Helper that builds the totals string ("Showing 1–10 of 142").
 */
export function paginationTotals({
  page,
  pageSize,
  total,
}: {
  page: number;
  pageSize: number;
  total: number;
}): string {
  if (total === 0) return "No results";
  const start = Math.min(total, Math.max(1, (page - 1) * pageSize + 1));
  const end = Math.min(total, page * pageSize);
  return `Showing ${start.toLocaleString()}\u2013${end.toLocaleString()} of ${total.toLocaleString()}`;
}
